import { getAccessToken } from '../utils/authToken';

function resolveWsBaseUrl(): string | null {
  let raw = process.env.REACT_APP_WS_BASE_URL?.trim();

  if (!raw && process.env.REACT_APP_API_URL) {
    try {
      const u = new URL(process.env.REACT_APP_API_URL.trim());
      raw = `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`;
    } catch {
      /* ignore */
    }
  }

  if (!raw) {
    // eslint-disable-next-line no-console
    console.warn(
      '[WebSocket] Set REACT_APP_WS_BASE_URL or REACT_APP_API_URL in .env and restart the dev server. Notifications WS disabled.',
    );
    return null;
  }

  return raw.replace(/\/+$/, '');
}

function buildNotificationsWsUrl(accessToken: string): string | null {
  const base = resolveWsBaseUrl();
  if (!base) return null;
  return `${base}/ws/notifications/?token=${encodeURIComponent(accessToken)}`;
}

export type NotificationWsPayload = {
  type?: string;
  id?: number;
  title?: string;
  body?: string;
  kind?: string;
  message?: string;
  data?: unknown;
  notification?: { title?: string; body?: string };
};

export function connectNotificationSocket(
  onNotification: (payload: NotificationWsPayload | Record<string, unknown>) => void,
): () => void {
  const accessToken = getAccessToken();
  const url = accessToken ? buildNotificationsWsUrl(accessToken) : null;

  if (!url) {
    return () => {};
  }

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closedByUser = false;
  let attempt = 0;

  const connect = () => {
    if (closedByUser) return;
    const token = getAccessToken();
    const nextUrl = token ? buildNotificationsWsUrl(token) : null;
    if (!nextUrl) return;

    ws = new WebSocket(nextUrl);
    ws.onopen = () => {
      attempt = 0;
    };
    ws.onmessage = async ev => {
      let text: string;
      const raw = ev.data;
      if (typeof raw === 'string') {
        text = raw;
      } else if (raw instanceof Blob) {
        text = await raw.text();
      } else {
        text = String(raw);
      }
      try {
        const data = JSON.parse(text) as NotificationWsPayload;
        onNotification(data);
      } catch {
        onNotification({ body: text, message: text });
      }
    };
    ws.onerror = () => {
      ws?.close();
    };
    ws.onclose = () => {
      ws = null;
      if (closedByUser) return;
      const delay = Math.min(30_000, 1000 * Math.pow(2, Math.min(attempt++, 5)));
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  connect();

  return () => {
    closedByUser = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    ws?.close();
    ws = null;
  };
}
